import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@design-system/Sheet';
import { useGetScoreReference } from '@viome/viome-base/src/usecase/Health/HealthQuery.ts';
import { isHexColor } from '@utils/checkHexCode.ts';
import { InfoIcon } from '@/components/icons/InfoIcon';
import { sortHealthReferenceByRankScore } from '@/utils/sortHealthReferenceByRankScore';
import { cn } from '@utils/styles';

interface HealthReferenceSectionProps {
  scoreId: number;
  variant?: string;
}

export const HealthReferenceSection = ({
  scoreId,
  variant='dark',
}: HealthReferenceSectionProps) => {
  const { data } = useGetScoreReference(scoreId);

  const { score_range: scoreRange = [], score_samples: scoreSamples = [] } =
    data || {};
  const sortedScoreRanges = sortHealthReferenceByRankScore(scoreRange);


  return (
    <Sheet>
      <SheetTrigger className="mr-1.5 flex items-center">
        <div className={cn("pr-1", variant === 'light' && 'text-secondary typography-btn-small')}>
          Reference
        </div>
        <InfoIcon
          className="h-2.5 w-2.5"
          aria-label="Information about the reference score"
        />
      </SheetTrigger>
      <SheetContent className="dark w-full md:w-[400px]">
        <SheetHeader>
          <SheetTitle>Score Reference</SheetTitle>
          <SheetDescription className="pl-0">
            <>
              {sortedScoreRanges?.length > 0 && (
                <>
                  <div className="typography-title">Score Ranges</div>
                  {sortedScoreRanges?.map((range) => {
                    const {
                      high_range,
                      low_range,
                      percentile,
                      rank,
                      rank_color,
                    } = range || {};
                    return (
                      <div className="mt-6">
                        <div className="flex items-center">
                          <div
                            style={{
                              color: isHexColor(rank_color)
                                ? rank_color
                                : undefined,
                            }}
                            className="typography-body mr-3"
                          >
                            {rank}
                          </div>
                          <div className="typography-body">
                            {low_range} - {high_range}
                          </div>
                        </div>
                        <div className="typography-body mt-3">
                          Represents {percentile}% of the Viome population
                        </div>
                      </div>
                    );
                  })}
                </>
              )}
            </>
            {scoreSamples?.length > 0 && (
              <>
                <div className="typography-title pt-10 mb-4">
                  Sample Analyzed
                </div>
                <span className="typography-body">{scoreSamples?.join()}</span>
              </>
            )}
          </SheetDescription>
        </SheetHeader>
      </SheetContent>
    </Sheet>
  );
};
